import {
  Coupon,
  CouponList,
  CouponsApi,
  CouponType,
  ErrorResponse,
} from '@citruscamps/citrus-client'
import { Dispatch, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
} from 'react-query'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
  UnlimitedPagination,
} from '../../../../../constants/pagination'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useRouter } from '../../../../../hooks/useRouter'
import { useSearchValue } from '../../../../../hooks/useSearchValue'
import { DateRangeType } from '../../../../../interfaces/filter-range'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateListKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { FilterDateRange } from '../../../../Events/pages/ManageEvents/hooks/filter-range'
import { useUpdateCoupon } from '../../CouponDetails/hooks/useUpdateCoupon'

export const DefaultCouponFetchProps: CouponFetchProps = {
  sort: 'updated',
  order: 'DESC',
  filter: {},
}

export interface CouponFilterProps {
  is_enabled?: boolean
  type?: CouponType[]
  redeemed?: FilterDateRange
  redeemed_type?: DateRangeType
  redeem_by?: FilterDateRange
  redeem_by_type?: DateRangeType
  membership_packages?: string[]
}

export interface CouponFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: CouponFilterProps
}

interface IProps {
  programId?: string
  pagination?: Pagination
  fetchProps?: CouponFetchProps
  overwriteFilters?: boolean
  enabled?: boolean
}

interface FetchCoupons {
  data: Coupon[]
  error: ErrorResponse | null
  fetchProps: CouponFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handleEnable: (coupon: Coupon) => Promise<void>
  handleDelete: (coupon: Coupon) => Promise<void>
  setFetchProps: (value?: CouponFetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchCoupons = ({
  programId,
  pagination: initialPagination,
  overwriteFilters,
  enabled = true,
  ...props
}: IProps): FetchCoupons => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const [pagination, setPagination] = useState<Pagination>(
    initialPagination || DefaultMainPagination,
  )
  const initialFetchProps: CouponFetchProps = props.fetchProps || DefaultCouponFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<CouponFetchProps>(
    `coupon-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination || DefaultMainPagination),
  })
  const queryKeys = generateListKey({
    type: 'coupon',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<CouponList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new CouponsApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      const resp: CouponList = await requestHandler<CouponList>(() =>
        client.findCoupons(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order || undefined,
            sort: fetchProps.sort || undefined,
            search: deferSearch || undefined,
            is_enabled: fetchProps?.filter.is_enabled,
            type:
              fetchProps?.filter.type && fetchProps?.filter.type.length > 0
                ? fetchProps?.filter.type
                : undefined,
            redeemed_lt: fetchProps.filter?.redeemed?.lt
              ? new Date(fetchProps.filter?.redeemed?.lt)
              : undefined,
            redeemed_gt: fetchProps.filter?.redeemed?.gt
              ? new Date(fetchProps.filter?.redeemed?.gt)
              : undefined,
            redeemed_lte: fetchProps.filter?.redeemed?.lte
              ? new Date(fetchProps.filter?.redeemed?.lte)
              : undefined,
            redeemed_gte: fetchProps.filter?.redeemed?.gte
              ? new Date(fetchProps.filter?.redeemed?.gte)
              : undefined,
            redeem_by_lt: fetchProps.filter?.redeem_by?.lt
              ? new Date(fetchProps.filter?.redeem_by?.lt)
              : undefined,
            redeem_by_gt: fetchProps.filter?.redeem_by?.gt
              ? new Date(fetchProps.filter?.redeem_by?.gt)
              : undefined,
            redeem_by_lte: fetchProps.filter?.redeem_by?.lte
              ? new Date(fetchProps.filter?.redeem_by?.lte)
              : undefined,
            redeem_by_gte: fetchProps.filter?.redeem_by?.gte
              ? new Date(fetchProps.filter?.redeem_by?.gte)
              : undefined,
            membership_packages:
              !!fetchProps.filter?.membership_packages &&
              fetchProps.filter.membership_packages?.length > 0
                ? fetchProps.filter.membership_packages
                : undefined,
          },
          { signal },
        ),
      )
      return resp
    },
    { enabled: !!programId && enabled },
  )
  const { handleEnable, handleDelete } = useUpdateCoupon({ programId })

  const _setFetchProps = (val?: CouponFetchProps) => {
    setPagination(initialPagination || DefaultMainPagination)
    return setFetchProps(val || DefaultCouponFetchProps)
  }

  return {
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    handleEnable,
    handleDelete,
    setFetchProps: _setFetchProps,
    setPagination,
    setSearch,
  }
}

interface FetchInfiniteCoupon {
  data: Coupon[]
  error: ErrorResponse | null
  fetchProps: CouponFetchProps
  isError: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  hasNextPage: boolean
  pagination: Pagination
  search: string
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<CouponList, ErrorResponse>>
  handleEnable: (coupon: Coupon) => Promise<void>
  handleDelete: (coupon: Coupon) => Promise<void>
  setFetchProps: (value?: CouponFetchProps) => void
  setSearch: Dispatch<string>
}

export const useFetchInfiniteCoupons = ({
  programId = '',
  pagination = DefaultDetailsPagination,
  overwriteFilters,
  ...props
}: IProps): FetchInfiniteCoupon => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const initialFetchProps: CouponFetchProps = props.fetchProps || DefaultCouponFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<CouponFetchProps>(
    `coupons-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('')
  const queryKeys = generateListKey({
    type: 'coupon',
    program_id: programId,
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    data,
    error,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<CouponList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg = pagination }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new CouponsApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const resp: CouponList = await requestHandler<CouponList>(() =>
        client.findCoupons(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order || undefined,
            sort: fetchProps.sort || undefined,
            search: deferSearch || undefined,
            is_enabled: fetchProps?.filter.is_enabled,
            type:
              fetchProps?.filter.type && fetchProps?.filter.type.length > 0
                ? fetchProps?.filter.type
                : undefined,
            redeemed_lt: fetchProps.filter?.redeemed?.lt
              ? new Date(fetchProps.filter?.redeemed?.lt)
              : undefined,
            redeemed_gt: fetchProps.filter?.redeemed?.gt
              ? new Date(fetchProps.filter?.redeemed?.gt)
              : undefined,
            redeemed_lte: fetchProps.filter?.redeemed?.lte
              ? new Date(fetchProps.filter?.redeemed?.lte)
              : undefined,
            redeemed_gte: fetchProps.filter?.redeemed?.gte
              ? new Date(fetchProps.filter?.redeemed?.gte)
              : undefined,
            redeem_by_lt: fetchProps.filter?.redeem_by?.lt
              ? new Date(fetchProps.filter?.redeem_by?.lt)
              : undefined,
            redeem_by_gt: fetchProps.filter?.redeem_by?.gt
              ? new Date(fetchProps.filter?.redeem_by?.gt)
              : undefined,
            redeem_by_lte: fetchProps.filter?.redeem_by?.lte
              ? new Date(fetchProps.filter?.redeem_by?.lte)
              : undefined,
            redeem_by_gte: fetchProps.filter?.redeem_by?.gte
              ? new Date(fetchProps.filter?.redeem_by?.gte)
              : undefined,
            membership_packages:
              !!fetchProps.filter?.membership_packages &&
              fetchProps.filter.membership_packages?.length > 0
                ? fetchProps.filter.membership_packages
                : undefined,
          },
          { signal },
        ),
      )
      return resp
    },
    {
      getNextPageParam: (resp: CouponList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: CouponList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId,
    },
  )
  const { handleEnable, handleDelete } = useUpdateCoupon({ programId })

  const _setFetchProps = (val?: CouponFetchProps) => {
    return setFetchProps(val || DefaultCouponFetchProps)
  }

  const resp = [...(data?.pages || [])].pop()
  return {
    data: (data?.pages || []).reduce<Coupon[]>((list, p) => [...list, ...p.data], []) || [],
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    fetchNextPage,
    handleEnable,
    handleDelete,
    setFetchProps: _setFetchProps,
    setSearch,
  }
}
